import React, { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { Pagination } from "@sscs/types";
import {
  DataTable,
  Page,
  hasActiveFilters,
  useFilterSortPagination,
} from "@sscs/ui";
import {
  bytesToGB,
  compareValues,
  getQueryStatus,
  sortItems,
} from "@sscs/utils";
import { FULL_GROUPS_PATH, FULL_SINGLE_GROUP_PATH } from "Constants";
import {
  useCheckPaginationPage,
  useCurrentOrganization,
  useDialog,
  useLoginInfo,
} from "Hooks";
import { useGetBaseOrganizationUsageQuery } from "Services";
import { Group } from "Types";
import AddGroup from "../../components/AddGroup/AddGroup";
import GroupRow from "../../components/GroupRow/GroupRow";
import GroupsTableActionBar from "../../components/GroupsTableActionBar/GroupsTableActionBar";
import { useGroupsColumns } from "../../components/GroupsTableHeader/GroupsTableHeader";
import { useGetOrganizationQuery } from "../../services/organizations.api";
import { FilterGroups, SortGroups } from "../../types";

const initSort: SortGroups = {
  property: "group_name",
  reverse: false,
};

const initFilter: FilterGroups = {
  groupName: "",
};

const initPagination: Pagination = {
  currentPage: 1,
  itemsPerPage: 10,
};

const sortSpecialCases = {
  group_name: (first: Group, second: Group, reverse: boolean) =>
    compareValues(first.name, second.name, reverse),
};

const GroupsTable = (): React.JSX.Element => {
  const { organizationId, membersGroupId } = useParams();
  const navigate = useNavigate();
  const { isSSOLogin, hasLoaded } = useLoginInfo();
  const columns = useGroupsColumns();

  const dialog = useDialog();
  const currentOrganization = useCurrentOrganization();
  const { state } = useLocation();
  const urlGroupIds = membersGroupId ? [membersGroupId] : [];
  const expandedGroupIds: string[] = Array.isArray(state?.expandedGroupIds)
    ? state.expandedGroupIds
    : urlGroupIds;

  const allGroupsPath = `/${generatePath(FULL_GROUPS_PATH, {
    organizationId: organizationId ?? "",
  })}`;
  const groupDetailsPath = (groupId: string): string =>
    `/${generatePath(FULL_SINGLE_GROUP_PATH, {
      organizationId: organizationId ?? "",
      membersGroupId: groupId,
    })}`;

  const handleToggle = (groupId: string, willExpand: boolean): void => {
    const nextIds = willExpand
      ? [...expandedGroupIds.filter((id) => id !== groupId), groupId]
      : expandedGroupIds.filter((id) => id !== groupId);
    const latestId = nextIds.at(-1);
    navigate(latestId ? groupDetailsPath(latestId) : allGroupsPath, {
      state: { expandedGroupIds: nextIds },
    });
  };
  const {
    filter,
    sort,
    pagination,
    handleFilterChange: changeFilter,
    handleSortChange: changeSort,
    handlePaginationChange: changePagination,
  } = useFilterSortPagination(initFilter, initSort, initPagination);

  const {
    data: organizationData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetOrganizationQuery(
    currentOrganization
      ? { organizationId: currentOrganization.id }
      : skipToken,
  );

  const { data: organizationUsageData } = useGetBaseOrganizationUsageQuery({
    organizationId: currentOrganization?.id || "",
  });

  const totalPortalCapacityBytes =
    organizationUsageData?.total_portal_capacity_bytes;

  const totalPortalCapacity = useMemo(
    () =>
      totalPortalCapacityBytes !== undefined
        ? bytesToGB(totalPortalCapacityBytes)
        : undefined,
    [totalPortalCapacityBytes],
  );

  const filteredGroups = (organizationData?.groups ?? []).filter((group) =>
    group.name.toLowerCase().includes(filter.groupName.toLowerCase()),
  );
  const sortedGroups = sortItems<Group, "member_count", "group_name">(
    filteredGroups,
    sort,
    sortSpecialCases,
  );
  const urlGroupIndex = sortedGroups.findIndex(
    (group) => group.group_id === membersGroupId,
  );
  const currentPage =
    urlGroupIndex >= 0
      ? Math.floor(urlGroupIndex / pagination.itemsPerPage) + 1
      : pagination.currentPage;
  const paginatedGroups = sortedGroups.slice(
    (currentPage - 1) * pagination.itemsPerPage,
    currentPage * pagination.itemsPerPage,
  );

  const collapseAll = (): void => {
    if (expandedGroupIds.length > 0) {
      navigate(allGroupsPath);
    }
  };
  const handleFilterChange = (nextFilter: FilterGroups): void => {
    collapseAll();
    changeFilter(nextFilter);
  };
  const handleSortChange = (nextSort: SortGroups): void => {
    collapseAll();
    changeSort(nextSort);
  };
  const handlePaginationChange = (nextPagination: Pagination): void => {
    collapseAll();
    changePagination(nextPagination);
  };

  useCheckPaginationPage({
    matchCount: sortedGroups.length,
    itemLength: paginatedGroups.length,
    currentPage,
    itemsPerPage: pagination.itemsPerPage,
    handlePaginationChange,
  });

  return (
    <>
      <Page.Filters
        found={{
          id: "groups-clear-filters",
          count: sortedGroups.length,
          initialFilters: initFilter,
          activeFilters: filter,
          onClear: () => handleFilterChange(initFilter),
        }}
      >
        <GroupsTableActionBar
          filter={filter}
          onFilterChange={handleFilterChange}
          onAddGroup={dialog.open}
        />
      </Page.Filters>
      <Page.Body>
        <DataTable
          columns={columns}
          sort={sort}
          onSortChange={handleSortChange}
          empty={{
            subject: "groups",
            filters: {
              active: hasActiveFilters(initFilter, filter),
              onReset: () => handleFilterChange(initFilter),
            },
          }}
          status={getQueryStatus({
            data: organizationData,
            isLoading,
            isFetching,
            isError,
            error,
          })}
          pagination={{
            itemCount: sortedGroups.length,
            pagination: { ...pagination, currentPage },
            onChange: handlePaginationChange,
          }}
        >
          {paginatedGroups.map((group) => (
            <GroupRow
              key={group.group_id}
              group={group}
              expanded={expandedGroupIds.includes(group.group_id)}
              totalPortalCapacity={totalPortalCapacity}
              onToggle={(willExpand) =>
                handleToggle(group.group_id, willExpand)
              }
            />
          ))}
        </DataTable>
      </Page.Body>
      <AddGroup
        isOpen={dialog.isOpen}
        onClose={dialog.close}
        showSSOGroupMapping={isSSOLogin && hasLoaded}
        totalPortalCapacity={totalPortalCapacity}
      />
    </>
  );
};

export default GroupsTable;
